/* eslint-disable prefer-template */
/* eslint-disable prefer-const */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 ~ Copyright 2020 Adobe Systems Incorporated
 ~
 ~ Licensed under the Apache License, Version 2.0 (the "License");
 ~ you may not use this file except in compliance with the License.
 ~ You may obtain a copy of the License at
 ~
 ~     http://www.apache.org/licenses/LICENSE-2.0
 ~
 ~ Unless required by applicable law or agreed to in writing, software
 ~ distributed under the License is distributed on an "AS IS" BASIS,
 ~ WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 ~ See the License for the specific language governing permissions and
 ~ limitations under the License.
 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

import React from 'react';

import isEqual from 'lodash/isEqual';
import { Helmet } from 'react-helmet-async';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { MapTo, Page, withComponentMappingContext } from '@adobe/aem-react-editable-components';

import { HOME_TYPE } from '@yme/common/constants';
import {
  PixelPageView,
  setPixelPageView,
  TRACKED_CONTENT_PAGES,
  TRACKED_OTHER_PAGES,
} from '@yme/utils/bloomreach';
import { interceptExternalRedirect } from '@yme/utils/common';

import { setPageProperties } from '../../common/redux/common/action';
import { setDigitalData } from '../../utils/digital-data';
import { withRoute } from '../RouteHelper/RouteHelper';

require('./Page.css');

// This component is a variant of a Page component mapped to the
// "yme/components/page" resource type. For now, the rendering is
// the same as the RootPage; this is more for illustration purposes
class AppPage extends Page {
  get containerProps() {
    let attrs = super.containerProps;

    attrs.className = (attrs.className || '') + ' page ' + (this.props.cssClassNames || '');

    return attrs;
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.webData, this.props.webData)) {
      setDigitalData('content-page-view', this.props.webData);
    }
  }

  componentDidMount() {
    const {
      isRightToLeft,
      daysToShowNew,
      isShowDriverLicenseEnabled,
      is35kwFilterDisabled,
      isInAuthor,
      currentCountryRoot,
      currentLanguageRoot,
      commercetoolsProjectKey,
      onSetPageProperties,
      language,
      languageRootPath,
      labels,
      blockStrategy,
      pageLevelNavigation,
      isTestRideEnabled,
      cqType,
      externalCart,
      locale,
      isoCountryCode,
      ecommerceEnabled,
      isInstallmentEnabled,
      isCycleSchemeEnabled,
      isNavDarkModeEnabled,
      isAuthenticationEnabled,
      locationMismatchModalModel,
      configuratorLeadFormPagePath,
      isNewsletterSubscriptionEnabled,
      isSearchEnabled,
      isMotorcycleVariationsEnabled,
      isKioskVersion,
      dealerLocatorPageUrl,
      cqPath,
      title,
      brandSlug,
      financeCalculatorLeadFormPagePath,
      globalSearchItemId,
      bloomreachModel,
      searchResultsPageUrl,
      backgroundImage,
      terminalPOIID,
      saleID,
      kioskPreferredDealers,
      isOmnibusRegulationEnabled,
      isNavLoggedInIndicatorEnabled,
      is70thLogoEnabled,
    } = this.props;

    onSetPageProperties({
      isRightToLeft,
      daysToShowNew,
      isShowDriverLicenseEnabled,
      is35kwFilterDisabled,
      isInAuthor,
      currentCountryRoot,
      currentLanguageRoot,
      commercetoolsProjectKey,
      language,
      languageRootPath,
      labels,
      pageBlockStrategy: blockStrategy, // Common brand-color for the page
      pageLevelNavigation,
      isTestRideEnabled,
      pageType: cqType,
      externalCart,
      locale,
      isoCountryCode,
      financeCalculatorLeadFormPagePath,
      ecommerceEnabled,
      isInstallmentEnabled,
      isCycleSchemeEnabled,
      isNavDarkModeEnabled,
      isAuthenticationEnabled,
      locationMismatchModalModel,
      configuratorLeadFormPagePath,
      isNewsletterSubscriptionEnabled,
      isSearchEnabled,
      isMotorcycleVariationsEnabled,
      isKioskVersion,
      dealerLocatorPageUrl,
      cqPath,
      bloomreachModel,
      searchResultsPageUrl,
      backgroundImage,
      kioskPreferredDealers,
      isOmnibusRegulationEnabled,
      isNavLoggedInIndicatorEnabled,
      is70thLogoEnabled,
    });

    // Temp solution for KIOSK
    if (terminalPOIID && saleID) {
      try {
        window.sessionStorage.setItem(
          'kioskTerminalPayload',
          JSON.stringify({
            terminalPOIID,
            saleID,
          }),
        );

        window.localStorage.setItem(
          'kioskPreferredDealerIds',
          JSON.stringify(kioskPreferredDealers),
        );
      } catch (error) {
        window.console.error(error);
      }
    }
    // Push the custom data layer
    setDigitalData('content-page-view', this.props.webData);

    if (!isInAuthor && TRACKED_CONTENT_PAGES.includes(cqType)) {
      const brandName = brandSlug ? ` - ${brandSlug}` : '';

      setPixelPageView(PixelPageView.CONTENT, {
        catalogs: [
          {
            name: bloomreachModel?.contentCatalog,
          },
        ],
        item_id: globalSearchItemId,
        item_name: `${title}${brandName}`,
        domain_key: bloomreachModel?.productCatalog,
      });
    }

    if (!isInAuthor && TRACKED_OTHER_PAGES.includes(cqType)) {
      const brandName = brandSlug ? ` - ${brandSlug}` : '';

      setPixelPageView(PixelPageView.OTHER, {
        catalogs: [
          {
            name: bloomreachModel?.contentCatalog,
          },
        ],
        item_id: globalSearchItemId,
        item_name: `${title}${brandName}`,
        domain_key: bloomreachModel?.productCatalog,
      });
    }

    if (!isInAuthor && HOME_TYPE === cqType) {
      const brandName = brandSlug ? ` - ${brandSlug}` : '';

      setPixelPageView(PixelPageView.HOME, {
        catalogs: [
          {
            name: bloomreachModel?.contentCatalog,
          },
        ],
        item_id: globalSearchItemId,
        item_name: `${title}${brandName}`,
        domain_key: bloomreachModel?.productCatalog,
      });
    }
  }

  render() {
    const redirectUrl = this.props.redirectTargetUrl;
    const title = `${this.props.title}${this.props.brandSlug ? ` - ${this.props.brandSlug}` : ''}`;

    if (interceptExternalRedirect(redirectUrl)) {
      return null;
    }

    return (
      <>
        {redirectUrl && <Redirect to={redirectUrl} />}
        <Helmet>
          <title>{title}</title>
          <meta
            name="description"
            content={this.props?.description || ''}
            data-react-helmet="true"
          />
          <link
            rel="canonical"
            href={this.props?.canonical}
            data-react-helmet="true"
          />
          <meta
            name="template"
            content={this.props?.templateName}
            data-react-helmet="true"
          />
          {HOME_TYPE === this.props?.cqType && (
            <meta
              name="google-site-verification"
              content="leZo7yqY_mxSAsObCtuneC5Hem4xl5wkbur08XppoVc"
              data-react-helmet="true"
            />
          )}
        </Helmet>
        {this.childComponents}
        {this.childPages}
      </>
    );
  }
}

const PageComponent = connect(null, {
  onSetPageProperties: setPageProperties,
})(AppPage);

export default MapTo([
  'yme/components/pages/page',
  'yme/components/pages/xfpage',
  'yme/components/pages/spa',
  'yme/components/pages/category-landing-page',
  'yme/components/pages/product-listing-page',
  'yme/components/pages/language-root-page',
  'yme/components/pages/home-page',
  'yme/components/pages/comparison-page',
  'yme/components/pages/content-page',
  'yme/components/pages/error-403-page',
  'yme/components/pages/error-404-page',
  'yme/components/pages/news-event-page',
  'yme/components/pages/news-content-listing-page',
  'yme/components/pages/events-content-listing-page',
  'yme/components/pages/shopping-cart-page',
  'yme/components/pages/dealer-locator-page',
  'yme/components/pages/checkout-page',
  'yme/components/pages/return-form-page',
  'yme/components/pages/for-owners-pdp-card-xf-page',
  'yme/components/pages/accessories-category-landing-page',
  'yme/components/pages/accessories-product-listing-page',
  'yme/components/pages/clothing-and-merchandise-category-landing-page',
  'yme/components/pages/clothing-and-merchandise-product-listing-page',
  'yme/components/pages/megamenu-offers-xf-page',
  'yme/components/pages/nav-content-page',
  'yme/components/pages/faq-item-xf-page',
  'yme/components/pages/owner-manuals-page',
  'yme/components/pages/offers-promo-listing-page',
  'yme/components/pages/offers-promo-page',
  'yme/components/pages/cookie-settings-page',
  'yme/components/pages/test-ride-category-landing-page',
  'yme/components/pages/test-ride-product-listing-page',
  'yme/components/pages/test-ride-form-page',
  'yme/components/pages/maintenance-page',
  'yme/components/pages/experiences-listing-page',
  'yme/components/pages/partners-listing-page',
  'yme/components/pages/partner-details-page',
  'yme/components/pages/ambassadors-listing-page',
  'yme/components/pages/ambassador-details-page',
  'yme/components/pages/wishlist-page',
  'yme/components/pages/privacy-notice-page',
  'yme/components/pages/newsletter-subscription-confirmation-page',
  'yme/components/pages/global-search-results-page',
  'yme/components/pages/kiosk-landing-page',
  'yme/components/pages/rental-locator-page',
])(withComponentMappingContext(withRoute(PageComponent)));
